import type { VariantProps } from 'class-variance-authority'
import { cva } from 'class-variance-authority'
import type * as React from 'react'
import { cn } from '@/lib/styles'
import { TAG_TEST_IDS } from '@/lib/test-ids'
import { getColorVariant, type ValidColorKey } from '@/utils/colorMapping'

const tagVariants = cva('rounded-full border', {
	variants: {
		variant: {
			'yellow-700': 'bg-yellow-700 text-neutral-light-100 border-yellow-700 font-semibold',
			'green-800': 'bg-green-800 text-neutral-light-100 border-green-800 font-semibold',
			'blue-700': 'bg-fjord-blue-700 text-neutral-light-100 border-fjord-blue-700 font-semibold',
			'peach-500': 'bg-peach-500 text-neutral-light-100 border-peach-500 font-bold',
			'peach-600': 'bg-peach-600 text-neutral-light-100 border-peach-600 font-semibold',
			'gray-500': 'bg-gray-500 text-neutral-light-100 border-gray-500 font-semibold',
			'green-success': 'bg-green-success text-neutral-light-100 border-green-success font-semibold',

			'green-200': 'bg-green-200 border-green-200 font-normal text-brand-black',
			'yellow-200': 'bg-yellow-200 border-yellow-200 font-normal text-brand-black',
			'purple-300': 'bg-purple-300 border-purple-300 font-normal text-brand-black',
			'peach-300': 'bg-peach-300 border-peach-300 font-normal text-brand-black',
			'gray-300': 'bg-gray-300 border-gray-300 font-normal text-brand-black',
			'teal-200': 'bg-teal-200 border-teal-200 font-normal text-brand-black',
			'gradient-secondary': 'gradient-secondary border-0 text-brand-black',
		},
		size: {
			medium: 'lok-text-body-bold-medium px-3 py-0.75',
			small: 'lok-text-body-bold-small px-2 py-0.75 lg:py-0.5',
		},
	},
	compoundVariants: [
		{
			variant: 'gradient-secondary',
			size: 'medium',
			className: 'lok-text-body-medium-medium px-5 py-2',
		},
	],
	defaultVariants: {
		variant: 'green-800',
		size: 'medium',
	},
})

export interface TagProps
	extends React.HTMLAttributes<HTMLSpanElement>,
		VariantProps<typeof tagVariants> {
	active?: boolean
	name?: string | null
	customColors?: {
		backgroundColor?: ValidColorKey | null
		textColor?: ValidColorKey | null
		borderColor?: ValidColorKey | null
	}
	fontWeight?: string
}

function Tag({
	className,
	variant,
	size,
	name,
	active,
	customColors,
	fontWeight = 'font-normal',
	...props
}: TagProps) {
	const customBorderColor = getColorVariant(customColors?.borderColor, 'border')
	const customDefaultBorderColor = getColorVariant(customColors?.backgroundColor, 'border')
	const customBackgroundColor = getColorVariant(customColors?.backgroundColor, 'bg')
	const customTextColor = getColorVariant(customColors?.textColor, 'text', 'brand-black')
	const customBorderColorWithState = active ? customBorderColor : customDefaultBorderColor

	return name ? (
		<span
			className={cn(
				'whitespace-nowrap h-fit',
				tagVariants({ variant, size, className }),
				customColors
					? [customBackgroundColor, customTextColor, customBorderColorWithState, fontWeight]
					: undefined,
			)}
			{...props}
			data-test-id={TAG_TEST_IDS.COMPONENT}
		>
			{name}
		</span>
	) : null
}

Tag.displayName = 'Tag'
export { Tag, tagVariants }
